import { useMemo, useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import type { ConviteCriado, ConviteLinha } from '@triade/contracts';
import { useMe } from '@/api/me';
import { useReenviarConvite, useRevogarConvite, useUsuarios } from '@/api/usuarios';
import { BarraDeAcoesLista, usePaginarLista } from '@/components/barra-lista';
import { ErroFormacao } from '@/components/formacao/avisos-formacao';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { ApiError } from '@/lib/api';
import { formatarContagem } from '@/lib/format';
import { BotaoRelatorios } from '@/relatorios';
import { ConvitesTabela } from './convites-tabela';
import { DialogoConvite, ResultadoConvite } from './dialogo-convite';
import { UsuarioDetalhe } from './usuario-detalhe';
import { UsuariosTabela } from './usuarios-tabela';
import { filtrarConvites, filtrarUsuarios, podeConceder, type BuscaUsuarios } from './tipos';

function Mensagem({ children, papel }: { children: React.ReactNode; papel?: 'status' }) {
  return (
    <p role={papel} className="px-4 py-10 text-center text-small text-ink-muted">
      {children}
    </p>
  );
}

/**
 * Tela "Usuarios e acessos" (/configuracoes/usuarios): grade de usuarios e de convites, formulario de acessos por empresa e dialogo de
 * convite (com e sem SMTP). A tela so esconde opcao; quem nega o que passa do teto do papel e a API.
 */
export function UsuariosPage({ busca }: { busca: BuscaUsuarios }) {
  const navigate = useNavigate();
  const me = useMe();
  const dados = useUsuarios();
  const reenviar = useReenviarConvite();
  const revogar = useRevogarConvite();
  const [convidando, setConvidando] = useState(false);
  const [reenvio, setReenvio] = useState<ConviteCriado | null>(null);
  const [erroConvite, setErroConvite] = useState<string | null>(null);
  const [ocupadoId, setOcupadoId] = useState<string | null>(null);

  const aba = busca.aba === 'convites' ? 'convites' : 'usuarios';
  const mudar = (m: Partial<BuscaUsuarios> & { aba?: 'convites' | undefined }) => void navigate({ to: '/configuracoes/usuarios', search: (a) => ({ ...(a as BuscaUsuarios), ...m }), replace: true });

  const usuarios = useMemo(() => filtrarUsuarios(dados.data?.usuarios ?? [], busca.q), [dados.data, busca.q]);
  const convites = useMemo(() => filtrarConvites(dados.data?.convites ?? [], busca.q), [dados.data, busca.q]);
  const pagUsuarios = usePaginarLista(usuarios);
  const pagConvites = usePaginarLista(convites);
  const pendentes = (dados.data?.convites ?? []).filter((c) => c.situacao === 'PENDENTE').length;
  const posicao = busca.usuario ? usuarios.findIndex((u) => u.id === busca.usuario) : -1;
  const selecionado = posicao >= 0 ? usuarios[posicao] : undefined;
  const opcoes = dados.data?.opcoes;
  const podeConvidar = podeConceder(opcoes);

  async function reenviarConvite(c: ConviteLinha) {
    setErroConvite(null);
    setOcupadoId(c.id);
    try {
      setReenvio(await reenviar.mutateAsync(c.id));
    } catch (e) {
      setErroConvite(e instanceof ApiError ? `${e.message}${e.code ? ` (${e.code})` : ''}` : 'Falha de comunicação com a API');
    } finally {
      setOcupadoId(null);
    }
  }
  async function revogarConvite(c: ConviteLinha) {
    setErroConvite(null);
    setOcupadoId(c.id);
    try {
      await revogar.mutateAsync(c.id);
    } catch (e) {
      setErroConvite(e instanceof ApiError ? `${e.message}${e.code ? ` (${e.code})` : ''}` : 'Falha de comunicação com a API');
    } finally {
      setOcupadoId(null);
    }
  }

  let corpo;
  if (dados.isError && !dados.data) corpo = <div className="p-4"><ErroFormacao erro={dados.error} onTentar={() => void dados.refetch()} /></div>;
  else if (!dados.data) corpo = <Mensagem papel="status">Carregando usuários…</Mensagem>;
  else if (aba === 'usuarios') {
    corpo =
      usuarios.length === 0 ? (
        <Mensagem papel="status">
          {busca.q ? 'Nenhum usuário com essa busca.' : 'Nenhum usuário nesta empresa ainda.'}
          {podeConvidar && !busca.q && (
            <>
              {' '}
              <button type="button" className="font-medium text-primary hover:underline" onClick={() => setConvidando(true)}>
                Convide o primeiro
              </button>
              .
            </>
          )}
        </Mensagem>
      ) : (
        <UsuariosTabela usuarios={pagUsuarios.visiveis} selecionadoId={busca.usuario} onAbrir={(u) => mudar({ usuario: u.id })} />
      );
  } else {
    corpo =
      convites.length === 0 ? (
        <Mensagem papel="status">{busca.q ? 'Nenhum convite com essa busca.' : 'Nenhum convite ainda.'}</Mensagem>
      ) : (
        <ConvitesTabela convites={pagConvites.visiveis} ocupadoId={ocupadoId} onReenviar={(c) => void reenviarConvite(c)} onRevogar={(c) => void revogarConvite(c)} />
      );
  }

  return (
    <div className="flex w-full flex-col gap-5 pb-4">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-small text-ink-muted">
            Configurações <span aria-hidden="true">/</span>{' '}
            <Link to="/configuracoes/usuarios" className="text-ink-muted hover:underline">
              Usuários e acessos
            </Link>
          </div>
          <h1 className="mt-1 text-display font-semibold tracking-[-0.01em]">Usuários e acessos</h1>
          <p className="mt-1 text-ink-secondary">Quem entra no sistema, com quais papéis e em quais empresas. Você concede até o seu próprio nível.</p>
        </div>
      </div>

      {me.data && me.data.permissoes.includes('usuarios.gerenciar') && dados.data && !podeConvidar && (
        <p role="note" className="rounded-xl border border-border bg-line-soft px-4 py-3 text-small text-ink-body">
          Seu perfil nesta empresa vê os usuários, mas não pode conceder acessos.
        </p>
      )}

      <section aria-label="Lista" className="rounded-2xl border border-border bg-card">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-line-soft px-4 py-3">
          <div role="tablist" aria-label="Visão" className="inline-flex rounded-lg bg-line-soft p-0.5">
            <button type="button" role="tab" aria-selected={aba === 'usuarios'} onClick={() => mudar({ aba: undefined })} className={`rounded-md px-3 py-1 text-small ${aba === 'usuarios' ? 'bg-card font-semibold shadow-xs' : 'text-ink-secondary'}`}>
              Usuários{dados.data ? ` (${formatarContagem(dados.data.usuarios.length)})` : ''}
            </button>
            <button type="button" role="tab" aria-selected={aba === 'convites'} onClick={() => mudar({ aba: 'convites', usuario: undefined })} className={`rounded-md px-3 py-1 text-small ${aba === 'convites' ? 'bg-card font-semibold shadow-xs' : 'text-ink-secondary'}`}>
              Convites{dados.data ? ` (${formatarContagem(pendentes)} pendentes)` : ''}
            </button>
          </div>
          <Input
            aria-label="Buscar usuário ou convite"
            placeholder="Nome, e-mail ou papel"
            className="h-9 max-w-[280px]"
            defaultValue={busca.q ?? ''}
            onChange={(e) => mudar({ q: e.target.value.trim() || undefined })}
          />
        </div>
        {erroConvite && (
          <p role="alert" className="mx-4 mt-3 rounded-md bg-destructive/10 px-3 py-2 text-small text-danger-ink">
            {erroConvite}
          </p>
        )}
        {corpo}
      </section>

      {aba === 'usuarios' && selecionado && opcoes && (
        <UsuarioDetalhe
          key={selecionado.id}
          usuario={selecionado}
          opcoes={opcoes}
          index={posicao + 1}
          total={usuarios.length}
          onAnterior={posicao > 0 ? () => mudar({ usuario: usuarios[posicao - 1]!.id }) : undefined}
          onProximo={posicao < usuarios.length - 1 ? () => mudar({ usuario: usuarios[posicao + 1]!.id }) : undefined}
          onLista={() => mudar({ usuario: undefined })}
          onConvidar={() => setConvidando(true)}
        />
      )}
      {aba === 'usuarios' && busca.usuario && !selecionado && dados.data && (
        <p role="status" className="rounded-xl border border-border bg-line-soft px-4 py-3 text-small text-ink-body">
          Esse usuário não está na lista (fora da sua empresa ou removido).
        </p>
      )}

      {dados.data && !selecionado && (
        <BarraDeAcoesLista
          novo={podeConvidar ? { rotulo: 'Convidar usuário', onClick: () => setConvidando(true) } : undefined}
          paginacao={aba === 'usuarios' ? pagUsuarios.paginacao : pagConvites.paginacao}
          relatorios={<BotaoRelatorios tela="usuarios" />}
        />
      )}

      {opcoes && <DialogoConvite aberto={convidando} aoFechar={() => setConvidando(false)} opcoes={opcoes} />}

      <Dialog open={reenvio !== null} onOpenChange={(o) => !o && setReenvio(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Convite reenviado</DialogTitle>
            <DialogDescription>O link anterior deixou de valer.</DialogDescription>
          </DialogHeader>
          {reenvio && <ResultadoConvite resultado={reenvio} />}
          <DialogFooter>
            <Button type="button" onClick={() => setReenvio(null)}>
              Concluir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
